import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import type { ReactNode, ComponentType } from 'react';
import App from './App';
import { GamePage } from './pages/GamePage';
import { LobbyPage } from './pages/LobbyPage';
import { RoomPage } from './pages/RoomPage';
import { EducationPage } from './pages/EducationPage';
import { HomePage } from './pages/HomePage';
import { AuthPage } from './pages/AuthPage';
import { ProfilePage } from './pages/ProfilePage';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { TutorialPage } from './pages/TutorialPage';
import { DailyPage } from './pages/DailyPage';
import { FriendsPage } from './pages/FriendsPage';
import { PublicProfilePage } from './pages/PublicProfilePage';
import { ChallengePage } from './pages/ChallengePage';
import { PremiumPage } from './pages/PremiumPage';
import { WalletPage } from './pages/WalletPage';
import { TournamentsPage } from './pages/TournamentsPage';
import BattlePassPage from './pages/BattlePassPage';
import ReferralPage from './pages/ReferralPage';
import { TournamentPlayPage } from './pages/TournamentPlayPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { ShopPage } from './pages/ShopPage';
import { SeasonRewardsPage } from './pages/SeasonRewardsPage';
import { RoomNotifier } from './components/RoomNotifier';
import { OfflineSync } from './components/OfflineSync';
import { SiteFooter } from './components/SiteFooter';
import { NicknameModal } from './components/NicknameModal';
import { useI18n } from './i18n';
import { useAuth } from './auth';
import { useGuestMode } from './hooks/useGuestMode';
import { getLocaleFromLocalePath, detectLocaleFromPath, ROUTE_SLUGS, type RouteKey } from './i18n/routes';
import { ALL_LOCALES, isLocale, type Locale } from './i18n/locales';
import { trackPageView } from './lib/analytics';

const AdminContentPage = lazy(() => import('./pages/AdminContentPage'));
const DownloadPage = lazy(() => import('./pages/DownloadPage'));
const OcrAdminPage = lazy(() => import('./pages/OcrAdminPage'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AnalyticsTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!pathname.startsWith('/admin')) {
      trackPageView(pathname, detectLocaleFromPath(pathname), search);
    }
  }, [pathname, search]);
  return null;
}

/** URL dili açıkça belirtiyorsa i18n state'i senkronize eder; ortak sayfalarda seçili dili korur. */
function LocaleSync() {
  const { pathname } = useLocation();
  const { locale, setLocale } = useI18n();
  useEffect(() => {
    const urlLocale = getLocaleFromLocalePath(pathname);
    if (!urlLocale) return;
    if (urlLocale !== locale) setLocale(urlLocale);
  }, [pathname, locale, setLocale]);
  return null;
}

/** /education/:technique → /egitim/:technique */
function LegacyEduRedirect() {
  const { pathname } = useLocation();
  const m = /^\/education\/(.+)$/.exec(pathname);
  return <Navigate to={m ? `/egitim/${m[1]}` : '/egitim'} replace />;
}

/** Legacy redirect that preserves query string. */
function LegacyRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function NicknameGate() {
  const { user } = useAuth();
  if (!(user as any)?.needs_nickname) return null;
  return <NicknameModal />;
}

function FooterOnHome() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { isGuest } = useGuestMode();
  const isHome = ALL_LOCALES.some(loc => {
    const home = ROUTE_SLUGS[loc].home;
    return pathname === home || pathname === `${home}/`;
  });
  if (!isHome) return null;
  if (user || isGuest) return null;
  return <SiteFooter />;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }
  return children;
}

// Casual çok oyunculu: üye VEYA misafir erişebilir. Misafir modu kapalı yeni
// ziyaretçi /auth'a yönlenir, oradan "misafir devam et" ile geri döner (from).
function RequireAuthOrGuest({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const { isGuest } = useGuestMode();
  const location = useLocation();
  if (loading) return null;
  if (!user && !isGuest) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }
  return children;
}

/**
 * Tüm dillerde aynı slug deseniyle tekrar eden içerik sayfaları.
 * Her RouteKey için ALL_LOCALES döngüsüyle route üretilir — yeni bir dil
 * canlıya çıkarken (LIVE_LOCALES'e eklenirken) burada HİÇBİR değişiklik
 * gerekmez, çünkü route zaten önizleme amacıyla mevcuttur (sadece
 * LangToggle/sitemap/prerender'da görünmez).
 */
const CONTENT_PAGE_KEYS: RouteKey[] = ['home', 'play', 'daily', 'solver', 'education', 'tutorial', 'leaderboard', 'shop', 'privacy', 'download'];

const CONTENT_PAGES: Partial<Record<RouteKey, ComponentType>> = {
  home: HomePage,
  play: GamePage,
  daily: DailyPage,
  solver: App,
  education: EducationPage,
  tutorial: TutorialPage,
  leaderboard: LeaderboardPage,
  shop: ShopPage,
  privacy: PrivacyPage,
  download: DownloadRoute,
};

function DownloadRoute() {
  const { t } = useI18n();
  // Capture the preference before URL locale sync or a lazy module can change it.
  const [entryLocale] = useState<Locale>(() => {
    const stored = localStorage.getItem('sudoku_lang');
    const browser = navigator.languages.map(value => value.toLowerCase().split('-')[0]).find(isLocale);
    return stored && isLocale(stored) ? stored : browser || 'en';
  });
  return <Suspense fallback={<p role="status">{t.common.loading}</p>}><DownloadPage entryLocale={entryLocale} /></Suspense>;
}

function buildContentRoutes(): ReactNode[] {
  const nodes: ReactNode[] = [];
  for (const loc of ALL_LOCALES) {
    for (const key of CONTENT_PAGE_KEYS) {
      const Comp = CONTENT_PAGES[key];
      if (!Comp) continue;
      const base = ROUTE_SLUGS[loc][key];
      nodes.push(<Route key={`${loc}-${key}`} path={base} element={<Comp />} />);
      if (key === 'education') {
        nodes.push(<Route key={`${loc}-${key}-param`} path={`${base}/:technique`} element={<Comp />} />);
      }
    }
  }
  return nodes;
}

export default function AppRouter() {
  const { t } = useI18n();
  return (
    <>
    <ScrollToTop />
    <LocaleSync />
    <AnalyticsTracker />
    <RoomNotifier />
    <OfflineSync />
    <NicknameGate />
    <Routes>
      {/* ── Locale-bazlı içerik sayfaları (tüm diller, bkz. buildContentRoutes) ── */}
      {buildContentRoutes()}

      {/* ── TR-only legacy yönlendirmeler ('/topluluk' artık ana sayfaya redirect) ── */}
      <Route path="/topluluk" element={<LegacyRedirect to="/" />} />
      <Route path="/en/community" element={<LegacyRedirect to="/en" />} />

      {/* ── Legacy → TR slug (301 davranışı: replace=true), query string korunur ──── */}
      <Route path="/play" element={<LegacyRedirect to="/oyna" />} />
      <Route path="/daily" element={<LegacyRedirect to="/gunluk" />} />
      <Route path="/solver" element={<LegacyRedirect to="/cozucu" />} />
      <Route path="/education" element={<LegacyRedirect to="/egitim" />} />
      <Route path="/education/:technique" element={<LegacyEduRedirect />} />
      <Route path="/tutorial" element={<LegacyRedirect to="/nasil-oynanir" />} />
      <Route path="/leaderboard" element={<LegacyRedirect to="/siralama" />} />
      <Route path="/privacy" element={<LegacyRedirect to="/gizlilik" />} />

      {/* ── Noindex ortak sayfalar (tüm dillerde aynı URL) ────── */}
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/lobby" element={<RequireAuthOrGuest><LobbyPage /></RequireAuthOrGuest>} />
      <Route path="/room/:roomId" element={<RequireAuthOrGuest><RoomPage /></RequireAuthOrGuest>} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/friends" element={<FriendsPage />} />
      <Route path="/user/:userId" element={<PublicProfilePage />} />
      <Route path="/challenge" element={<RequireAuth><ChallengePage /></RequireAuth>} />
      <Route path="/premium" element={<PremiumPage />} />
      <Route path="/wallet" element={<WalletPage />} />
      <Route path="/tournaments" element={<TournamentsPage />} />
      <Route path="/tournament/:id" element={<TournamentPlayPage />} />
      <Route path="/battlepass" element={<BattlePassPage />} />
      <Route path="/referral" element={<ReferralPage />} />
      <Route path="/sezon-odulleri" element={<SeasonRewardsPage />} />
      <Route path="/admin/ocr" element={<Suspense fallback={<p role="status">{t.common.loading}</p>}><OcrAdminPage /></Suspense>} />
      <Route path="/admin/content" element={<Suspense fallback={<p role="status">{t.common.loading}</p>}><AdminContentPage /></Suspense>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <FooterOnHome />
    </>
  );
}
